import React from 'react'

import { closeToast } from '@/entities/toast'
import { Toast, ToastContent } from '@/shared/components/Toast/Toast'
import { ToastContext, useToastContext, useToastState } from '@/shared/components/Toast/ToastContext'
import { useAppDispatch, useAppSelector } from '@/shared/store'

export type GlobalToastProviderProps = {
  children: React.ReactNode
}

export const GlobalToastProvider: React.FC<GlobalToastProviderProps> = ({ children }) => {
  const toastState = useToastState()

  return (
    <ToastContext.Provider value={toastState}>
      <React.Fragment>
        {children}
        <GlobalToast />
      </React.Fragment>
    </ToastContext.Provider>
  )
}

/*
  This component is used to display a global toast message. There are two ways to raise a toast message:
    1. Using the useToastContext hook to set the toast content at the ui level
    2. Dispatching a toast action from the store inside a store action or thunk
  This is done to ensure that the toast message can be raised from either a component or a store action.
*/
const GlobalToast: React.FC = (props) => {
  // From the context (1)
  const { toastContent, setToastContent } = useToastContext()

  // From the store (2)
  const globalToastContent = useAppSelector(state => state.toast)
  const dispatch = useAppDispatch()

  const onClose = () => {
    setToastContent((prev) => ({ ...prev, open: false } as ToastContent))
    dispatch(closeToast())
  }

  return (
    <>
      <Toast
        open={toastContent?.open ?? false}
        onClose={onClose}
        severity={toastContent?.severity ?? 'info'}
        title={toastContent?.title ?? ''}
        content={toastContent?.content ?? ''}
        {...props}
      />
      <Toast
        open={globalToastContent.open}
        onClose={onClose}
        severity={globalToastContent.severity}
        title={globalToastContent.title}
        content={globalToastContent.content}
        {...props}
      />
    </>
  )
}